import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  Plus, Search, Users, Phone, Mail, Calendar,
  MessageSquare, ChevronRight, X,
  Lightbulb, ArrowRight, CheckCircle2, Upload, Edit2, Trash2,
  Sparkles, Zap, Brain, RefreshCw, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import { MainLayout } from '../components/MainLayout';
import { ScoreRing, AnimatedProgress } from '../components/ui/animated';
import { FollowUpRemindersPanel } from '../components/FollowUpRemindersPanel';
import { CalendarExportButton, CalendarSubscriptionDialog } from '../components/CalendarExport';
import { isContactPickerSupported, pickAndImportContacts } from '../utils/contactPicker';

const RELATIONSHIP_STATUS = [
  { id: 'cold', label: 'Cold', color: 'bg-slate-500' },
  { id: 'warm', label: 'Warm', color: 'bg-amber-500' },
  { id: 'hot', label: 'Hot', color: 'bg-red-500' },
  { id: 'client', label: 'Client', color: 'bg-emerald-500' },
  { id: 'dormant', label: 'Dormant', color: 'bg-slate-400' },
];

const SECTORS = [
  { id: 'commercial_fitout', label: 'Commercial Fit-out' },
  { id: 'commercial_office', label: 'Commercial Office' },
  { id: 'retail', label: 'Retail' },
  { id: 'residential', label: 'Residential' },
  { id: 'healthcare', label: 'Healthcare' },
  { id: 'education', label: 'Education' },
  { id: 'developer', label: 'Developer' },
  { id: 'contractor', label: 'Contractor' },
  { id: 'architect', label: 'Architect' },
  { id: 'other', label: 'Other' },
];

// Store dismissed prompts in session
const getDismissedPrompts = () => {
  try {
    return JSON.parse(sessionStorage.getItem('dismissedPipelinePrompts') || '[]');
  } catch {
    return [];
  }
};

const dismissPrompt = (contactId, type) => {
  const dismissed = getDismissedPrompts();
  const key = `${contactId}-${type}`;
  if (!dismissed.includes(key)) {
    dismissed.push(key);
    sessionStorage.setItem('dismissedPipelinePrompts', JSON.stringify(dismissed));
  }
};

const OUTREACH_TYPES = [
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'phone', label: 'Phone', icon: Phone },
  { id: 'meeting', label: 'Meeting', icon: Users },
  { id: 'linkedin', label: 'LinkedIn', icon: MessageSquare },
];

// Suggest next action based on contact status and history
const getSuggestedAction = (contact, outreachLogs) => {
  const daysSinceContact = contact.last_contact_date 
    ? Math.floor((Date.now() - new Date(contact.last_contact_date).getTime()) / (1000 * 60 * 60 * 24))
    : 999;
  
  // lastOutreach available for potential future recommendations logic
  const _lastOutreach = outreachLogs?.[0];
  
  if (contact.relationship_status === 'cold') {
    return {
      action: 'Make initial contact',
      type: 'phone',
      reason: 'No relationship established yet',
      urgency: 'medium',
    };
  }
  
  if (contact.relationship_status === 'warm') {
    if (daysSinceContact > 7) {
      return {
        action: 'Follow up to maintain momentum',
        type: 'email',
        reason: `Last contact was ${daysSinceContact} days ago`,
        urgency: 'high',
      };
    }
    return {
      action: 'Schedule a meeting to progress relationship',
      type: 'meeting',
      reason: 'Contact is warm - good time to meet',
      urgency: 'medium',
    };
  }
  
  if (contact.relationship_status === 'hot') {
    if (daysSinceContact > 3) {
      return {
        action: 'Urgent follow-up required',
        type: 'phone',
        reason: 'Hot lead not contacted recently',
        urgency: 'critical',
      };
    }
    return {
      action: 'Push for commitment or next step',
      type: 'meeting',
      reason: 'Contact is hot - time to close',
      urgency: 'high',
    };
  }
  
  if (contact.relationship_status === 'client') {
    if (daysSinceContact > 30) {
      return {
        action: 'Nurture existing relationship',
        type: 'email',
        reason: 'Keep client engaged for repeat business',
        urgency: 'low',
      };
    }
    return {
      action: 'Ask for referrals',
      type: 'phone',
      reason: 'Happy client - good source of referrals',
      urgency: 'low',
    };
  }
  
  if (contact.relationship_status === 'dormant') {
    return {
      action: 'Re-engage with value proposition',
      type: 'email',
      reason: 'Dormant contact - worth reviving',
      urgency: 'low',
    };
  }
  
  return {
    action: 'Review contact and decide next step',
    type: 'email',
    reason: 'General follow-up',
    urgency: 'medium',
  };
};

export const PipelineFullPage = () => {
  const { user, authFetch } = useAuth();
  const { leadScoreUpdates } = useWebSocket();
  const [contacts, setContacts] = useState([]);
  const [outreachLogs, setOutreachLogs] = useState([]);
  const [pipelinePrompts, setPipelinePrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('contacts');
  
  // Lead Scoring
  const [leadScores, setLeadScores] = useState({});
  const [topLeads, setTopLeads] = useState([]);
  const [scoringContact, setScoringContact] = useState(null);
  const [showLeadScoreModal, setShowLeadScoreModal] = useState(false);
  const [selectedLeadScore, setSelectedLeadScore] = useState(null);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showOutreachModal, setShowOutreachModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactOutreach, setContactOutreach] = useState([]);
  
  // Mobile detection for responsive modal
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // CSV Import
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  
  // Mobile Contact Import
  const [importingFromPhone, setImportingFromPhone] = useState(false);
  
  // Forms
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    position: '',
    sector: '',
    relationship_status: 'cold',
    source: '',
    notes: '',
  });
  
  const [editFormData, setEditFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    position: '',
    sector: '',
    relationship_status: 'cold',
    source: '',
    notes: '',
    next_follow_up: '',
  });
  
  const [outreachData, setOutreachData] = useState({
    type: 'email',
    direction: 'outbound',
    subject: '',
    summary: '',
    outcome: 'neutral',
    follow_up_required: false,
    follow_up_date: '',
  });

  useEffect(() => {
    loadData();
    loadTopLeads();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Listen for WebSocket lead score updates
  useEffect(() => {
    if (leadScoreUpdates.length > 0) {
      const latest = leadScoreUpdates[leadScoreUpdates.length - 1];
      setLeadScores(prev => ({
        ...prev,
        [latest.contact_id]: { total_score: latest.total_score, grade: latest.grade }
      }));
    }
  }, [leadScoreUpdates]);

  const loadData = async () => {
    try {
      const [contactsRes, outreachRes, promptsRes] = await Promise.all([
        authFetch('/pipeline/contacts'),
        authFetch('/pipeline/outreach/all'),
        authFetch('/prompts/pipeline')
      ]);
      
      if (contactsRes.ok) {
        const data = await contactsRes.json();
        setContacts(data.contacts || []);
      }
      
      if (outreachRes.ok) {
        const data = await outreachRes.json();
        setOutreachLogs(data.outreach_logs || []);
      }
      
      if (promptsRes.ok) {
        const data = await promptsRes.json();
        setPipelinePrompts(data.prompts || []);
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const loadTopLeads = async () => {
    try {
      const res = await authFetch('/leads/top?limit=5');
      if (res.ok) {
        const data = await res.json();
        setTopLeads(data.leads || []);
      }
    } catch (err) {
      console.error('Failed to load top leads:', err);
    }
  };
  
  const scoreContact = async (contactId) => {
    setScoringContact(contactId);
    try {
      const res = await authFetch(`/leads/${contactId}/score`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setLeadScores(prev => ({ ...prev, [contactId]: data }));
        setSelectedLeadScore(data);
        setShowLeadScoreModal(true);
        toast.success(`Lead scored: ${data.grade} (${data.total_score}/100)`);
        loadTopLeads();
      } else {
        toast.error('Failed to calculate lead score');
      }
    } catch (err) {
      toast.error('Failed to score lead');
    } finally {
      setScoringContact(null);
    }
  };
  
  const batchScoreLeads = async () => {
    toast.info('Scoring all contacts...');
    try {
      const res = await authFetch('/leads/batch-score?limit=50', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Scored ${data.scored} contacts`);
        loadTopLeads();
      }
    } catch (err) {
      toast.error('Batch scoring failed');
    }
  };

  const loadContactOutreach = async (contactId) => {
    try {
      const res = await authFetch(`/pipeline/outreach/${contactId}`);
      if (res.ok) {
        const data = await res.json();
        setContactOutreach(data.outreach_logs || []);
      }
    } catch (err) {
      console.error('Load error:', err);
    }
  };

  const openProfile = async (contact) => {
    setSelectedContact(contact);
    setContactOutreach([]);
    await loadContactOutreach(contact.id);
    setShowProfileModal(true);
  };

  // Import contacts from phone (Contact Picker API)
  const handleImportFromPhone = async () => {
    if (!isContactPickerSupported()) {
      toast.error('Contact Picker not supported on this device. Try from your mobile browser (Android Chrome).');
      return;
    }
    
    setImportingFromPhone(true);
    try {
      const result = await pickAndImportContacts(user?.token);
      if (result.imported_count > 0) {
        toast.success(`Imported ${result.imported_count} contact(s)`);
        loadData();
      }
      if (result.skipped_count > 0) {
        toast.info(`Skipped ${result.skipped_count} duplicate(s)`);
      }
      if (result.imported_count === 0 && result.skipped_count === 0) {
        toast.info('No contacts selected');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to import contacts');
    } finally {
      setImportingFromPhone(false);
    }
  };

  const createContact = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch('/pipeline/contacts', {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        toast.success('Contact created');
        setShowAddModal(false);
        resetForm();
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create contact');
      }
    } catch (err) {
      toast.error('Failed to create contact');
    }
  };

  const logOutreach = async (e) => {
    e.preventDefault();
    if (!selectedContact) return;
    
    try {
      const res = await authFetch('/pipeline/outreach', {
        method: 'POST',
        body: {
          contact_id: selectedContact.id,
          ...outreachData
        }
      });
      if (res.ok) {
        toast.success('Outreach logged');
        // Dismiss all prompts for this contact since we've taken action
        dismissPrompt(selectedContact.id, 'no_contact');
        dismissPrompt(selectedContact.id, 'follow_up_due');
        dismissPrompt(selectedContact.id, 'no_next_action');
        setShowOutreachModal(false);
        resetOutreachForm();
        loadData();
        if (showProfileModal) {
          await loadContactOutreach(selectedContact.id);
        }
      }
    } catch (err) {
      toast.error('Failed to log outreach');
    }
  };

  const updateContactStatus = async (contactId, newStatus) => {
    try {
      const res = await authFetch(`/pipeline/contacts/${contactId}`, {
        method: 'PATCH',
        body: { relationship_status: newStatus }
      });
      if (res.ok) {
        toast.success('Status updated');
        loadData();
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      contact_name: '',
      email: '',
      phone: '',
      position: '',
      sector: '',
      relationship_status: 'cold',
      source: '',
      notes: '',
    });
  };

  const resetOutreachForm = () => {
    setOutreachData({
      type: 'email',
      direction: 'outbound',
      subject: '',
      summary: '',
      outcome: 'neutral',
      follow_up_required: false,
      follow_up_date: '',
    });
  };

  // Edit Contact
  const openEditModal = (contact) => {
    setEditFormData({
      company_name: contact.company_name || '',
      contact_name: contact.contact_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      position: contact.position || '',
      sector: contact.sector || '',
      relationship_status: contact.relationship_status || 'cold',
      source: contact.source || '',
      notes: contact.notes || '',
      next_follow_up: contact.next_follow_up ? contact.next_follow_up.split('T')[0] : '',
    });
    setSelectedContact(contact);
    setShowEditModal(true);
  };

  const updateContact = async (e) => {
    e.preventDefault();
    if (!selectedContact) return;
    
    try {
      const res = await authFetch(`/pipeline/contacts/${selectedContact.id}`, {
        method: 'PUT',
        body: editFormData
      });
      if (res.ok) {
        toast.success('Contact updated');
        setShowEditModal(false);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update contact');
      }
    } catch (err) {
      toast.error('Failed to update contact');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      const res = await authFetch(`/pipeline/contacts/${contactId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Contact deleted');
        setShowProfileModal(false);
        setShowEditModal(false);
        loadData();
      } else {
        toast.error('Failed to delete contact');
      }
    } catch (err) {
      toast.error('Failed to delete contact');
    }
  };

  // CSV Import
  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
      
      const preview = lines.slice(1, 6).map(line => {
        const values = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || [];
        const cleanValues = values.map(v => v.replace(/"/g, '').trim());
        const row = {};
        headers.forEach((h, i) => {
          row[h] = cleanValues[i] || '';
        });
        return row;
      });
      
      setCsvPreview(preview);
    };
    reader.readAsText(file);
  };

  const importContacts = async () => {
    if (!csvFile) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', csvFile);
    
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pipeline/contacts/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('opsflow_token')}`
        },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Imported ${data.imported_count} contacts`);
        setShowImportModal(false);
        setCsvFile(null);
        setCsvPreview([]);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to import contacts');
      }
    } catch (err) {
      toast.error('Failed to import contacts');
    } finally {
      setImporting(false);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || contact.relationship_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Group outreach by date
  const groupedOutreach = outreachLogs.reduce((acc, log) => {
    const date = new Date(log.logged_at).toLocaleDateString('en-GB');
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {});

  const statusCounts = RELATIONSHIP_STATUS.reduce((acc, s) => {
    acc[s.id] = contacts.filter(c => c.relationship_status === s.id).length;
    return acc;
  }, { all: contacts.length });

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="pipeline-full-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1e3a5f]">
              Pipeline
            </h1>
            <p className="text-slate-500 mt-1">
              {contacts.length} contacts • {outreachLogs.length} activities logged
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={() => setShowCalendarModal(true)}
              variant="outline"
              className="rounded-full border-blue-200 text-blue-700 hover:bg-blue-50"
              data-testid="calendar-sync-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Calendar
            </Button>
            {(user?.role === 'director' || user?.role === 'admin') && (
              <Button
                onClick={batchScoreLeads}
                variant="outline"
                className="rounded-full border-violet-200 text-violet-700 hover:bg-violet-50"
                data-testid="batch-score-btn"
              >
                <Brain className="w-4 h-4 mr-2" />
                AI Score All
              </Button>
            )}
            
            {/* Import from Phone - only on supported devices */}
            <Button
              onClick={handleImportFromPhone}
              variant="outline"
              className="rounded-full"
              disabled={importingFromPhone}
              title={isContactPickerSupported() ? "Import from phone contacts" : "Contact Picker not supported - works on Android Chrome"}
            >
              <Smartphone className="w-4 h-4 mr-2" />
              {importingFromPhone ? 'Importing...' : 'From Phone'}
            </Button>
            
            <Button
              onClick={() => setShowImportModal(true)}
              variant="outline"
              className="rounded-full"
              data-testid="import-contacts-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full px-6"
              data-testid="add-contact-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          </div>
        </div>
        
        {/* Top Leads Card */}
        {topLeads.length > 0 && (
          <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-2xl p-5 border border-violet-100 animate-fade-in-up">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <h3 className="font-heading font-semibold text-[#1e3a5f]">Top Leads</h3>
              <span className="text-xs text-slate-500 ml-auto">AI-powered scoring</span>
            </div>
            <div className="grid grid-cols-5 gap-3">
              {topLeads.slice(0, 5).map((lead, idx) => (
                <div 
                  key={lead.contact_id}
                  className="bg-white rounded-xl p-3 hover-lift cursor-pointer border border-slate-100"
                  onClick={() => {
                    const contact = contacts.find(c => c.id === lead.contact_id);
                    if (contact) openProfile(contact);
                  }}
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`score-badge score-badge-${lead.grade?.[0]?.toLowerCase() || 'c'}`}>
                      {lead.grade}
                    </span>
                    <span className="text-lg font-bold text-[#1e3a5f]">{lead.total_score}</span>
                  </div>
                  <p className="text-sm font-medium text-slate-800 truncate">{lead.company_name || 'Unknown'}</p>
                  <p className="text-xs text-slate-500 truncate">{lead.contact_name || 'Unknown'}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Follow-up Reminders Panel */}
        <FollowUpRemindersPanel onRefresh={loadData} />

        {/* Pipeline Prompts */}
        {pipelinePrompts.length > 0 && (
          <div className="space-y-2">
            {pipelinePrompts
              .filter(prompt => {
                const dismissed = getDismissedPrompts();
                const key = `${prompt.contact_id}-${prompt.type}`;
                return !dismissed.includes(key);
              })
              .slice(0, 3)
              .map((prompt, idx) => (
              <div 
                key={idx}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl ${
                  prompt.priority === 'high' 
                    ? 'bg-amber-50 border border-amber-200' 
                    : 'bg-white border border-slate-200'
                }`}
              >
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  prompt.priority === 'high' ? 'bg-amber-500 animate-pulse' : 'bg-slate-400'
                }`} />
                <span 
                  onClick={() => {
                    if (prompt.contact_id) {
                      const contact = contacts.find(c => c.id === prompt.contact_id);
                      if (contact) openProfile(contact);
                    }
                  }}
                  className={`text-sm flex-1 cursor-pointer hover:underline ${
                    prompt.priority === 'high' ? 'text-amber-800 font-medium' : 'text-slate-600'
                  }`}
                >
                  {prompt.message}
                </span>
                {prompt.contact_id && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissPrompt(prompt.contact_id, prompt.type);
                      // Force re-render
                      setPipelinePrompts([...pipelinePrompts]);
                      toast.success('Prompt dismissed');
                    }}
                    className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                    title="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                {prompt.contact_id && (
                  <ChevronRight 
                    onClick={() => {
                      const contact = contacts.find(c => c.id === prompt.contact_id);
                      if (contact) openProfile(contact);
                    }}
                    className="w-4 h-4 text-slate-400 cursor-pointer hover:text-slate-600" 
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100 p-1 rounded-xl">
            <TabsTrigger value="contacts" className="rounded-lg">
              <Users className="w-4 h-4 mr-2" />
              Contacts
            </TabsTrigger>
            <TabsTrigger value="outreach" className="rounded-lg">
              <MessageSquare className="w-4 h-4 mr-2" />
              Outreach Log
            </TabsTrigger>
          </TabsList>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="space-y-6 mt-6">
            {/* Status Filters */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  statusFilter === 'all'
                    ? 'bg-[#1e3a5f] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                All ({statusCounts.all})
              </button>
              {RELATIONSHIP_STATUS.map(status => (
                <button
                  key={status.id}
                  onClick={() => setStatusFilter(statusFilter === status.id ? 'all' : status.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                    statusFilter === status.id
                      ? 'bg-[#1e3a5f] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full ${status.color}`} />
                  {status.label} ({statusCounts[status.id] || 0})
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search contacts..."
                className="pl-10 h-11 rounded-xl"
                data-testid="search-input"
              />
            </div>

            {/* Contacts Table View - Better Visibility */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Contact</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                      <th className="text-center px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Score</th>
                      <th className="text-right px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">View</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredContacts.map(contact => {
                      const status = RELATIONSHIP_STATUS.find(s => s.id === contact.relationship_status);
                      const suggested = getSuggestedAction(contact, []);
                      const lastContact = contact.last_contact_date 
                        ? new Date(contact.last_contact_date).toLocaleDateString('en-GB')
                        : 'Never';
                      const contactScore = leadScores[contact.id];
                      
                      return (
                        <tr 
                          key={contact.id}
                          className="hover:bg-slate-50 transition-colors cursor-pointer animate-fade-in-up"
                          onClick={() => openProfile(contact)}
                          data-testid={`contact-row-${contact.id}`}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${status?.color || 'bg-slate-400'}`}>
                                {contact.contact_name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <p className="font-medium text-[#1e3a5f]">{contact.contact_name}</p>
                                {contact.position && (
                                  <p className="text-xs text-slate-500">{contact.position}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-slate-700">{contact.company_name}</p>
                            {contact.sector && (
                              <p className="text-xs text-slate-400 capitalize">{contact.sector}</p>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${status?.color || 'bg-slate-400'} text-white`}>
                              {status?.label || 'Unknown'}
                            </span>
                            {suggested.urgency === 'critical' && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 animate-pulse">
                                Urgent
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-slate-600">{lastContact}</p>
                            {contact.next_follow_up && (
                              <p className="text-xs text-amber-600 font-medium">
                                Follow-up: {new Date(contact.next_follow_up).toLocaleDateString('en-GB')}
                              </p>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0" />
                              <span className="text-sm text-slate-600 line-clamp-2">{suggested.action}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            {contactScore ? (
                              <div 
                                className="inline-flex items-center gap-1.5 cursor-pointer hover:scale-105 transition-transform"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  scoreContact(contact.id);
                                }}
                              >
                                <span className={`score-badge score-badge-${contactScore.grade?.[0]?.toLowerCase() || 'c'}`}>
                                  {contactScore.grade}
                                </span>
                              </div>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  scoreContact(contact.id);
                                }}
                                disabled={scoringContact === contact.id}
                              >
                                {scoringContact === contact.id ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Brain className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-500 hover:text-[#1e3a5f] hover:bg-slate-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditModal(contact);
                                }}
                                data-testid={`edit-contact-${contact.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <CalendarExportButton 
                                type="followup" 
                                itemId={contact.id} 
                                compact={true}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-[#1e3a5f] hover:bg-[#1e3a5f]/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openProfile(contact);
                                }}
                              >
                                View
                                <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {filteredContacts.length === 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-slate-700 mb-2">
                  No contacts found
                </h3>
                <p className="text-slate-500">
                  {searchQuery || statusFilter !== 'all' 
                    ? 'Try adjusting your filters'
                    : 'Add your first contact to get started'}
                </p>
              </div>
            )}
          </TabsContent>

          {/* Outreach Tab */}
          <TabsContent value="outreach" className="space-y-6 mt-6">
            {Object.keys(groupedOutreach).length > 0 ? (
              Object.entries(groupedOutreach).map(([date, logs]) => (
                <div key={date} className="space-y-3">
                  <h3 className="text-sm font-medium text-slate-500 sticky top-0 bg-[#f8fafc] py-2">
                    {date}
                  </h3>
                  {logs.map(log => {
                    const contact = contacts.find(c => c.id === log.contact_id);
                    const TypeIcon = OUTREACH_TYPES.find(t => t.id === log.type)?.icon || MessageSquare;
                    
                    return (
                      <div 
                        key={log.id}
                        className="bg-white rounded-xl border border-slate-100 p-4 flex items-start gap-4"
                      >
                        <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                          <TypeIcon className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-slate-800">
                              {contact?.contact_name || 'Unknown'}
                            </span>
                            <span className="text-slate-400">•</span>
                            <span className="text-sm text-slate-500 capitalize">
                              {log.type} ({log.direction})
                            </span>
                          </div>
                          {log.subject && (
                            <p className="text-sm font-medium text-slate-700">{log.subject}</p>
                          )}
                          {log.summary && (
                            <p className="text-sm text-slate-500 mt-1">{log.summary}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                            <span className={`chip ${
                              log.outcome === 'positive' ? 'bg-emerald-500' :
                              log.outcome === 'negative' ? 'bg-red-500' :
                              log.outcome === 'no_response' ? 'bg-slate-400' :
                              'bg-amber-500'
                            } text-white`}>
                              {log.outcome?.replace('_', ' ')}
                            </span>
                            <span>
                              {new Date(log.logged_at).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-slate-700 mb-2">
                  No outreach logged yet
                </h3>
                <p className="text-slate-500">
                  Start logging your outreach activities
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Contact Profile - Side Panel (Better UX than Modal) */}
        {showProfileModal && selectedContact && (
          <div className="fixed inset-0 z-50 flex">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/30"
              onClick={() => setShowProfileModal(false)}
            />
            
            {/* Side Panel */}
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="font-heading text-xl font-bold text-[#1e3a5f]">Contact Profile</h2>
                <button 
                  onClick={() => setShowProfileModal(false)}
                  className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Contact Info */}
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-[#1e3a5f] rounded-2xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                    {selectedContact.contact_name?.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-bold text-[#1e3a5f]">
                      {selectedContact.contact_name}
                    </h3>
                    <p className="text-slate-600">{selectedContact.company_name}</p>
                    {selectedContact.position && (
                      <p className="text-sm text-slate-500">{selectedContact.position}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Select
                        value={selectedContact.relationship_status}
                        onValueChange={(value) => {
                          updateContactStatus(selectedContact.id, value);
                          setSelectedContact({ ...selectedContact, relationship_status: value });
                        }}
                      >
                        <SelectTrigger className="w-32 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RELATIONSHIP_STATUS.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Suggested Next Action */}
                {(() => {
                  const suggested = getSuggestedAction(selectedContact, contactOutreach);
                  return (
                    <div className={`p-4 rounded-xl ${
                      suggested.urgency === 'critical' ? 'bg-red-50 border border-red-200' :
                      suggested.urgency === 'high' ? 'bg-amber-50 border border-amber-200' :
                      'bg-sky-50 border border-sky-200'
                    }`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <Lightbulb className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                            suggested.urgency === 'critical' ? 'text-red-500' :
                            suggested.urgency === 'high' ? 'text-amber-500' :
                            'text-sky-500'
                          }`} />
                          <div>
                            <p className="font-medium text-slate-800">Suggested: {suggested.action}</p>
                            <p className="text-sm text-slate-600">{suggested.reason}</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => {
                            setOutreachData(prev => ({ ...prev, type: suggested.type }));
                            setShowProfileModal(false);
                            setShowOutreachModal(true);
                          }}
                          className="bg-[#1e3a5f] text-white rounded-full flex-shrink-0"
                        >
                          Log {suggested.type}
                        </Button>
                      </div>
                    </div>
                  );
                })()}

                {/* Contact Details */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Contact Details</h4>
                  <div className="space-y-2">
                    {selectedContact.email && (
                      <a 
                        href={`mailto:${selectedContact.email}`}
                        className="p-3 bg-slate-50 rounded-xl flex items-center gap-3 hover:bg-slate-100"
                      >
                        <Mail className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-[#1e3a5f]">{selectedContact.email}</span>
                      </a>
                    )}
                    {selectedContact.phone && (
                      <a 
                        href={`tel:${selectedContact.phone}`}
                        className="p-3 bg-slate-50 rounded-xl flex items-center gap-3 hover:bg-slate-100"
                      >
                        <Phone className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-[#1e3a5f]">{selectedContact.phone}</span>
                      </a>
                    )}
                  </div>
                </div>

                {selectedContact.notes && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Notes</h4>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-sm text-slate-600">{selectedContact.notes}</p>
                    </div>
                  </div>
                )}

                {/* Outreach History */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Outreach History</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowProfileModal(false);
                        setShowOutreachModal(true);
                      }}
                      className="rounded-full"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Log Outreach
                    </Button>
                  </div>
                  
                  {contactOutreach.length > 0 ? (
                    <div className="space-y-3">
                      {contactOutreach.map(log => {
                        const TypeIcon = OUTREACH_TYPES.find(t => t.id === log.type)?.icon || MessageSquare;
                        return (
                          <div key={log.id} className="p-3 bg-slate-50 rounded-xl flex items-start gap-3">
                            <TypeIcon className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium capitalize">{log.type}</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  log.outcome === 'positive' ? 'bg-emerald-100 text-emerald-700' :
                                  log.outcome === 'negative' ? 'bg-red-100 text-red-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                  {log.outcome}
                                </span>
                              </div>
                              {log.summary && (
                                <p className="text-sm text-slate-500 mt-1">{log.summary}</p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(log.logged_at).toLocaleDateString('en-GB')}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">
                      No outreach logged yet
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Contact Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f]">
                Add Contact
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={createContact} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name *</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Company *</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Position</Label>
                  <Input
                    value={formData.position}
                    onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sector</Label>
                  <Select
                    value={formData.sector}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, sector: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sector" />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTORS.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="min-h-[80px]"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#162c46] text-white">
                  Add Contact
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Log Outreach Modal - Uses Sheet on mobile (bottom drawer), Dialog on desktop */}
        {isMobile ? (
          <Sheet open={showOutreachModal} onOpenChange={setShowOutreachModal}>
            <SheetContent side="bottom" className="h-[90vh] p-0 rounded-t-2xl">
              {/* Navy Header */}
              <div className="bg-[#1e3a5f] px-4 py-4 relative overflow-hidden flex-shrink-0 rounded-t-2xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                <SheetHeader className="relative z-10">
                  <SheetTitle className="font-heading text-lg text-white flex items-center gap-3 pr-8">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <span className="block font-bold">Log Outreach</span>
                      <span className="text-xs font-normal text-white/70">Record your client interaction</span>
                    </div>
                  </SheetTitle>
                </SheetHeader>
              </div>
              
              <form onSubmit={logOutreach} className="p-4 space-y-4 bg-white overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 88px)' }}>
                {/* Contact Info Card */}
                {selectedContact && (
                  <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border border-slate-200">
                    <div className="w-10 h-10 bg-[#1e3a5f] rounded-xl flex items-center justify-center text-white text-base font-bold shadow-lg flex-shrink-0">
                      {selectedContact.contact_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1e3a5f] truncate">{selectedContact.contact_name}</p>
                      <p className="text-sm text-slate-500 truncate">{selectedContact.company_name}</p>
                    </div>
                    <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                      RELATIONSHIP_STATUS.find(s => s.id === selectedContact.relationship_status)?.color || 'bg-slate-400'
                    } text-white flex-shrink-0`}>
                      {RELATIONSHIP_STATUS.find(s => s.id === selectedContact.relationship_status)?.label || 'Unknown'}
                    </div>
                  </div>
                )}
                
                {/* Type Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider">Type</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {OUTREACH_TYPES.map(t => {
                      const Icon = t.icon;
                      const isSelected = outreachData.type === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setOutreachData(prev => ({ ...prev, type: t.id }))}
                          className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all duration-200 border-2 ${
                            isSelected
                              ? 'bg-[#1e3a5f] text-white border-[#1e3a5f] shadow-lg'
                              : 'bg-white text-slate-600 border-slate-200'
                          }`}
                        >
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                          <span className="text-xs font-semibold">{t.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Direction Toggle */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider">Direction</Label>
                  <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
                    <button
                      type="button"
                      onClick={() => setOutreachData(prev => ({ ...prev, direction: 'outbound' }))}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        outreachData.direction === 'outbound'
                          ? 'bg-[#1e3a5f] text-white shadow-md'
                          : 'text-slate-600 hover:bg-white/50'
                      }`}
                    >
                      <ArrowRight className="w-4 h-4" />
                      Outbound
                    </button>
                    <button
                      type="button"
                      onClick={() => setOutreachData(prev => ({ ...prev, direction: 'inbound' }))}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        outreachData.direction === 'inbound'
                          ? 'bg-[#1e3a5f] text-white shadow-md'
                          : 'text-slate-600 hover:bg-white/50'
                      }`}
                    >
                      <ArrowRight className="w-4 h-4 rotate-180" />
                      Inbound
                    </button>
                  </div>
                </div>
                
                {/* Subject */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider">Subject</Label>
                  <Input
                    value={outreachData.subject}
                    onChange={(e) => setOutreachData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Brief subject or topic..."
                    className="h-10 rounded-xl border-slate-200 bg-white"
                  />
                </div>
                
                {/* Summary */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider">Summary</Label>
                  <Textarea
                    value={outreachData.summary}
                    onChange={(e) => setOutreachData(prev => ({ ...prev, summary: e.target.value }))}
                    placeholder="What was discussed? Key takeaways?"
                    className="min-h-[60px] rounded-xl resize-none border-slate-200 bg-white"
                  />
                </div>
                
                {/* Outcome Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider">Outcome</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'positive', label: 'Positive', color: 'bg-emerald-500', ring: 'ring-emerald-500' },
                      { id: 'neutral', label: 'Neutral', color: 'bg-slate-500', ring: 'ring-slate-500' },
                      { id: 'negative', label: 'Negative', color: 'bg-red-500', ring: 'ring-red-500' },
                      { id: 'no_response', label: 'No Response', color: 'bg-amber-500', ring: 'ring-amber-500' },
                    ].map(o => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => setOutreachData(prev => ({ ...prev, outcome: o.id }))}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                          outreachData.outcome === o.id
                            ? `${o.color} text-white shadow-lg ring-2 ${o.ring} ring-offset-2`
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Follow-up Card */}
                <div className="p-3 bg-gradient-to-r from-sky-50 to-indigo-50 rounded-xl border border-sky-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-sky-600" />
                      <span className="font-semibold text-slate-800 text-sm">Schedule Follow-up?</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOutreachData(prev => ({ ...prev, follow_up_required: !prev.follow_up_required }))}
                      className={`w-12 h-7 rounded-full transition-all duration-300 ${
                        outreachData.follow_up_required ? 'bg-[#1e3a5f]' : 'bg-slate-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${
                        outreachData.follow_up_required ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  
                  {outreachData.follow_up_required && (
                    <div className="mt-3 pt-3 border-t border-sky-200">
                      <Input
                        type="date"
                        value={outreachData.follow_up_date}
                        onChange={(e) => setOutreachData(prev => ({ ...prev, follow_up_date: e.target.value }))}
                        className="h-10 rounded-lg bg-white border-sky-200"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex gap-3 pt-2 pb-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowOutreachModal(false)}
                    className="flex-1 rounded-xl h-11 border-slate-200"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl h-11 shadow-lg"
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Log Outreach
                  </Button>
                </div>
              </form>
            </SheetContent>
          </Sheet>
        ) : (
          <Dialog open={showOutreachModal} onOpenChange={setShowOutreachModal}>
            <DialogContent className="sm:max-w-lg p-0 overflow-hidden bg-white border-0 shadow-2xl max-h-[90dvh]">
              {/* Navy Header with Gradient Accent - Desktop */}
              <div className="bg-[#1e3a5f] px-6 py-5 relative overflow-hidden flex-shrink-0">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                <DialogHeader className="relative z-10">
                  <DialogTitle className="font-heading text-xl text-white flex items-center gap-3 pr-8">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <span className="block font-bold">Log Outreach</span>
                      <span className="text-sm font-normal text-white/70">Record your client interaction</span>
                    </div>
                  </DialogTitle>
                </DialogHeader>
              </div>
              
              <form onSubmit={logOutreach} className="p-6 space-y-5 bg-white overflow-y-auto flex-1">
                {/* Contact Info Card */}
                {selectedContact && (
                  <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl border border-slate-200">
                    <div className="w-12 h-12 bg-[#1e3a5f] rounded-2xl flex items-center justify-center text-white text-lg font-bold shadow-lg flex-shrink-0">
                      {selectedContact.contact_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1e3a5f] truncate">{selectedContact.contact_name}</p>
                      <p className="text-sm text-slate-500 truncate">{selectedContact.company_name}</p>
                    </div>
                    <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                      RELATIONSHIP_STATUS.find(s => s.id === selectedContact.relationship_status)?.color || 'bg-slate-400'
                    } text-white flex-shrink-0`}>
                      {RELATIONSHIP_STATUS.find(s => s.id === selectedContact.relationship_status)?.label || 'Unknown'}
                    </div>
                  </div>
                )}
                
                {/* Type Selection - Icon Cards */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider">Type</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {OUTREACH_TYPES.map(t => {
                      const Icon = t.icon;
                      const isSelected = outreachData.type === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setOutreachData(prev => ({ ...prev, type: t.id }))}
                          className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all duration-200 border-2 ${
                            isSelected
                              ? 'bg-[#1e3a5f] text-white border-[#1e3a5f] shadow-lg'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-[#1e3a5f]/30'
                          }`}
                        >
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                          <span className="text-xs font-semibold">{t.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Direction Toggle */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider">Direction</Label>
                  <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
                    <button
                      type="button"
                      onClick={() => setOutreachData(prev => ({ ...prev, direction: 'outbound' }))}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        outreachData.direction === 'outbound'
                          ? 'bg-[#1e3a5f] text-white shadow-md'
                          : 'text-slate-600 hover:bg-white/50'
                      }`}
                    >
                      <ArrowRight className="w-4 h-4" />
                      Outbound
                    </button>
                    <button
                      type="button"
                      onClick={() => setOutreachData(prev => ({ ...prev, direction: 'inbound' }))}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        outreachData.direction === 'inbound'
                          ? 'bg-[#1e3a5f] text-white shadow-md'
                          : 'text-slate-600 hover:bg-white/50'
                      }`}
                    >
                      <ArrowRight className="w-4 h-4 rotate-180" />
                      Inbound
                    </button>
                  </div>
                </div>
                
                {/* Subject */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider">Subject</Label>
                  <Input
                    value={outreachData.subject}
                    onChange={(e) => setOutreachData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Brief subject or topic..."
                    className="h-10 rounded-xl border-slate-200 bg-white"
                  />
                </div>
                
                {/* Summary */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider">Summary</Label>
                  <Textarea
                    value={outreachData.summary}
                    onChange={(e) => setOutreachData(prev => ({ ...prev, summary: e.target.value }))}
                    placeholder="What was discussed? Key takeaways?"
                    className="min-h-[80px] rounded-xl resize-none border-slate-200 bg-white"
                  />
                </div>
                
                {/* Outcome Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider">Outcome</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'positive', label: 'Positive', color: 'bg-emerald-500', ring: 'ring-emerald-500' },
                      { id: 'neutral', label: 'Neutral', color: 'bg-slate-500', ring: 'ring-slate-500' },
                      { id: 'negative', label: 'Negative', color: 'bg-red-500', ring: 'ring-red-500' },
                      { id: 'no_response', label: 'No Response', color: 'bg-amber-500', ring: 'ring-amber-500' },
                    ].map(o => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => setOutreachData(prev => ({ ...prev, outcome: o.id }))}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                          outreachData.outcome === o.id
                            ? `${o.color} text-white shadow-lg ring-2 ${o.ring} ring-offset-2`
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Follow-up Card */}
                <div className="p-3 bg-gradient-to-r from-sky-50 to-indigo-50 rounded-xl border border-sky-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-sky-600" />
                      <span className="font-semibold text-slate-800 text-sm">Schedule Follow-up?</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOutreachData(prev => ({ ...prev, follow_up_required: !prev.follow_up_required }))}
                      className={`w-12 h-7 rounded-full transition-all duration-300 ${
                        outreachData.follow_up_required ? 'bg-[#1e3a5f]' : 'bg-slate-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${
                        outreachData.follow_up_required ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  
                  {outreachData.follow_up_required && (
                    <div className="mt-3 pt-3 border-t border-sky-200">
                      <Input
                        type="date"
                        value={outreachData.follow_up_date}
                        onChange={(e) => setOutreachData(prev => ({ ...prev, follow_up_date: e.target.value }))}
                        className="h-10 rounded-lg bg-white border-sky-200"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowOutreachModal(false)}
                    className="flex-1 rounded-xl h-11 border-slate-200"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl h-11 shadow-lg"
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Log Outreach
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* Edit Contact Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f] flex items-center gap-2">
                <Edit2 className="w-5 h-5" />
                Edit Contact
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={updateContact} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company *</Label>
                  <Input
                    value={editFormData.company_name}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, company_name: e.target.value }))}
                    required
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Name *</Label>
                  <Input
                    value={editFormData.contact_name}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                    required
                    className="rounded-xl"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Position</Label>
                  <Input
                    value={editFormData.position}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, position: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sector</Label>
                  <Select
                    value={editFormData.sector}
                    onValueChange={(value) => setEditFormData(prev => ({ ...prev, sector: value }))}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select sector" />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTORS.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={editFormData.relationship_status}
                    onValueChange={(value) => setEditFormData(prev => ({ ...prev, relationship_status: value }))}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RELATIONSHIP_STATUS.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          <span className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${s.color}`} />
                            {s.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Next Follow-up</Label>
                  <Input
                    type="date"
                    value={editFormData.next_follow_up}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, next_follow_up: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Source</Label>
                <Input
                  value={editFormData.source}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, source: e.target.value }))}
                  placeholder="How did you find this contact?"
                  className="rounded-xl"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="min-h-[80px] rounded-xl"
                />
              </div>
              
              <div className="flex justify-between pt-4 border-t border-slate-100">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => deleteContact(selectedContact?.id)}
                  className="text-red-600 border-red-200 hover:bg-red-50 rounded-xl"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowEditModal(false)} className="rounded-xl">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl">
                    Save Changes
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* CSV Import Modal */}
        <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
          <DialogContent className="max-w-2xl max-h-[90dvh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f] flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Import Contacts from CSV
              </DialogTitle>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto space-y-6 mt-4">
              {/* Instructions */}
              <div className="p-4 bg-sky-50 rounded-xl border border-sky-200">
                <h4 className="font-semibold text-sky-800 mb-2">CSV Format Requirements</h4>
                <p className="text-sm text-sky-700 mb-2">
                  Your CSV should have these columns (headers in first row):
                </p>
                <code className="block text-xs bg-white p-2 rounded border border-sky-200 text-sky-900">
                  company_name, contact_name, email, phone, position, sector, relationship_status, source, notes
                </code>
                <p className="text-xs text-sky-600 mt-2">
                  * company_name and contact_name are required. Other fields are optional.
                </p>
              </div>
              
              {/* File Upload */}
              <div className="space-y-2">
                <Label>Select CSV File</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-[#1e3a5f]/30 transition-colors">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvFile}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                    <p className="font-medium text-slate-700">
                      {csvFile ? csvFile.name : 'Click to upload CSV'}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      {csvFile ? `${(csvFile.size / 1024).toFixed(1)} KB` : 'or drag and drop'}
                    </p>
                  </label>
                </div>
              </div>
              
              {/* Preview */}
              {csvPreview.length > 0 && (
                <div className="space-y-2">
                  <Label>Preview (first 5 rows)</Label>
                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Company</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Name</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Email</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Phone</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {csvPreview.map((row, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2 text-slate-700">{row.company_name || row.company || '-'}</td>
                            <td className="px-3 py-2 text-slate-700">{row.contact_name || row.name || '-'}</td>
                            <td className="px-3 py-2 text-slate-700">{row.email || '-'}</td>
                            <td className="px-3 py-2 text-slate-700">{row.phone || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-500">
                    {csvPreview.length < 5 ? `${csvPreview.length} contacts total` : 'Showing first 5 of many contacts'}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowImportModal(false);
                  setCsvFile(null);
                  setCsvPreview([]);
                }}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button 
                onClick={importContacts}
                disabled={!csvFile || importing}
                className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl"
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Contacts
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Lead Score Modal */}
        <Dialog open={showLeadScoreModal} onOpenChange={setShowLeadScoreModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f] flex items-center gap-2">
                <Brain className="w-5 h-5 text-violet-600" />
                Lead Score Analysis
              </DialogTitle>
            </DialogHeader>
            
            {selectedLeadScore && (
              <div className="space-y-6 mt-4">
                {/* Score Ring */}
                <div className="flex justify-center">
                  <ScoreRing 
                    score={selectedLeadScore.total_score} 
                    maxScore={100}
                    size={140}
                    label="Lead Score"
                  />
                </div>
                
                {/* Breakdown */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-slate-700">Score Breakdown</h4>
                  {selectedLeadScore.breakdown && Object.entries(selectedLeadScore.breakdown).map(([key, data]) => (
                    <div key={key} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">{data.label}</span>
                        <span className="font-medium text-[#1e3a5f]">{data.score}/{data.max}</span>
                      </div>
                      <AnimatedProgress 
                        value={data.score} 
                        max={data.max}
                        color={data.score >= data.max * 0.7 ? 'bg-emerald-500' : data.score >= data.max * 0.4 ? 'bg-amber-500' : 'bg-slate-400'}
                      />
                    </div>
                  ))}
                </div>
                
                {/* AI Insights */}
                {selectedLeadScore.ai_insights && (
                  <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl p-4 border border-violet-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-violet-600" />
                      <span className="text-sm font-semibold text-violet-700">AI Insight</span>
                    </div>
                    <p className="text-sm text-slate-700">{selectedLeadScore.ai_insights}</p>
                  </div>
                )}
                
                {/* Recommendations */}
                {selectedLeadScore.recommendations?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-slate-700">Recommended Actions</h4>
                    {selectedLeadScore.recommendations.map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <Zap className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">{rec}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                <Button
                  onClick={() => setShowLeadScoreModal(false)}
                  className="w-full bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full"
                >
                  Close
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Calendar Subscription Dialog */}
        <CalendarSubscriptionDialog 
          open={showCalendarModal} 
          onOpenChange={setShowCalendarModal} 
        />
      </div>
    </MainLayout>
  );
};
